/**
 * LocationDisclosureModal — Google Play "Prominent Disclosure" for
 * background location.
 *
 * Google Play policy requires that any app requesting background location
 * MUST show a plain-language, user-facing explanation of WHY location is
 * collected — BEFORE the OS-level permission prompt appears. The disclosure
 * must be non-dismissable except by explicit user action ("I Understand").
 *
 * Rules we enforce:
 *   - Shows automatically on the FIRST time a staff-role user (supervisor,
 *     crew_leader, agent, manager, driver) opens the Staff dashboard.
 *   - Persists acceptance in localStorage per-user so it never nags again.
 *   - Modal cannot be closed by clicking the backdrop or pressing Escape;
 *     the only way out is the "I Understand" button.
 *   - Wording matches Google Play Console verbatim so reviewers can
 *     screenshot the same sentence they typed into the disclosure form.
 *
 * If your app ever adds a native GPS permission request, call
 * `hasAcknowledgedLocationDisclosure()` FIRST — never request permission
 * before this returns true.
 */
import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { MapPin, Route, Shield, Battery } from 'lucide-react';

const STORAGE_KEY_PREFIX = 'necs_location_disclosure_ack_v1__';

const STAFF_ROLES = new Set([
  'supervisor', 'crew_leader', 'agent', 'manager', 'driver', 'staff',
]);

export function shouldShowLocationDisclosure(user) {
  if (!user || !STAFF_ROLES.has(user.role)) return false;
  try {
    return !localStorage.getItem(STORAGE_KEY_PREFIX + user.user_id);
  } catch (_err) {
    return true; // if storage is blocked, safest to show it
  }
}

export function hasAcknowledgedLocationDisclosure(user) {
  if (!user) return false;
  try {
    return !!localStorage.getItem(STORAGE_KEY_PREFIX + user.user_id);
  } catch (_err) {
    return false;
  }
}

export default function LocationDisclosureModal({ user, onAcknowledged }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setOpen(shouldShowLocationDisclosure(user));
  }, [user?.user_id, user?.role]);

  const ack = () => {
    try {
      localStorage.setItem(
        STORAGE_KEY_PREFIX + user.user_id,
        new Date().toISOString(),
      );
    } catch (_err) { /* ignore storage errors */ }
    setOpen(false);
    onAcknowledged?.();
  };

  if (!user) return null;

  return (
    <Dialog
      open={open}
      // Prominent-disclosure policy: modal MUST NOT be dismissable except
      // via the explicit "I Understand" button. We intercept every close
      // signal (Escape, outside click, close-icon) and keep it open.
      onOpenChange={() => { /* intentionally no-op */ }}
    >
      <DialogContent
        className="max-w-lg bg-zinc-950 border-2 border-[#FFD60A] text-white [&>button.absolute]:hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        data-testid="location-disclosure-modal"
        aria-describedby="staff-location-description"
      >
        <DialogHeader>
          <DialogTitle
            className="text-2xl flex items-center gap-2 text-[#FFD60A]"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          >
            <MapPin className="h-6 w-6" />
            LOCATION ACCESS &mdash; STAFF APP
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          <p
            className="text-base font-semibold text-white leading-relaxed"
            data-testid="location-disclosure-headline"
            id="staff-location-description"
          >
            This app collects location data to enable property arrival
            verification and route optimization even when the app is closed
            or not in use.
          </p>

          <div className="space-y-2">
            <p className="text-zinc-400 text-xs uppercase tracking-widest">
              Why we need this
            </p>
            <ul className="space-y-2 text-zinc-200">
              <li className="flex items-start gap-2">
                <Shield className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" />
                <span>
                  <strong>Property arrival verification.</strong> Confirms
                  you&apos;re physically on-site before starting a job &mdash;
                  keeps timesheets honest and pay accurate.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <Route className="h-4 w-4 text-sky-400 shrink-0 mt-0.5" />
                <span>
                  <strong>Route optimization.</strong> Sequences your next
                  stops by proximity, saving drive time and fuel.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <MapPin className="h-4 w-4 text-purple-400 shrink-0 mt-0.5" />
                <span>
                  <strong>Live progress visibility.</strong> Lets your
                  supervisor and residents see the truck&apos;s current
                  location during service.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <Battery className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />
                <span>
                  <strong>Background use.</strong> We keep collecting location
                  while your phone screen is off so we don&apos;t drop your
                  route between stops. We do <em>not</em> track you outside
                  your shift.
                </span>
              </li>
            </ul>
          </div>

          <div
            className="rounded-md bg-zinc-900 border border-zinc-700 p-3 text-xs text-zinc-400"
            data-testid="location-disclosure-privacy-note"
          >
            Your location is <strong>never</strong> sold, shared with
            advertisers, or used outside these four service functions. Data is
            encrypted in transit and retained for 90 days for operational
            auditing, then anonymized. See our{' '}
            <a
              href="/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#C9A962] hover:underline"
              data-testid="location-disclosure-privacy-link"
            >
              Privacy Policy
            </a>{' '}
            for full details.
          </div>

          <div className="flex justify-end pt-2">
            <Button
              onClick={ack}
              className="bg-[#FFD60A] hover:bg-[#EAB308] text-black font-bold w-full sm:w-auto"
              data-testid="location-disclosure-acknowledge-btn"
            >
              I Understand &mdash; continue
            </Button>
          </div>
          <p className="text-[10px] text-zinc-500 text-center">
            After tapping &quot;I Understand&quot;, your device will ask for
            location permission. Please choose <strong>&quot;Allow all the
            time&quot;</strong> (Android) or <strong>&quot;Always
            Allow&quot;</strong> (iOS) so the app can function during your
            shift.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
